import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Code, Copy, ExternalLink, CheckCircle, MessageCircle, Link } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface FormEmbedCodeProps {
  formId: string;
  formTitle: string;
  isPro: boolean;
}

const FormEmbedCode = ({ formId, formTitle, isPro }: FormEmbedCodeProps) => {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const formUrl = `${window.location.origin}/form/${formId}`;
  const whatsAppLink = `https://wa.me/2347088059321?text=${encodeURIComponent(`Start ${formId}`)}`;
  const brandedLink = `${window.location.origin}/f/${formId}`;

  const iframeCode = `<iframe 
  src="${formUrl}" 
  width="100%" 
  height="600" 
  frameborder="0"
  title="${formTitle}"
></iframe>`;

  const scriptCode = `<div id="bravax-form-${formId}"></div>
<script>
  (function() {
    const iframe = document.createElement('iframe');
    iframe.src = '${formUrl}';
    iframe.width = '100%';
    iframe.height = '600';
    iframe.frameBorder = '0';
    iframe.title = '${formTitle}';
    document.getElementById('bravax-form-${formId}').appendChild(iframe);
  })();
</script>`;

  const htmlCode = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${formTitle} - Contact Form</title>
</head>
<body>
  ${iframeCode}
</body>
</html>`;

  const handleCopy = (code: string, type: string) => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast({
      title: "Copied!",
      description: `${type} copied to clipboard`,
    });
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isPro) {
    return (
      <Card className="card-elegant">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center space-x-2">
                <Code className="h-5 w-5" />
                <span>Website Embedding & Branded Links</span>
              </CardTitle>
              <CardDescription>
                Embed this form on your website and share branded WhatsApp links
              </CardDescription>
            </div>
            <Badge variant="secondary">Pro Feature</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 space-y-4">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
              <Code className="h-8 w-8 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">Upgrade to Pro</h3>
              <p className="text-muted-foreground mb-4">
                Embed forms on your website, get branded links, and receive WhatsApp notifications automatically.
              </p>
              <Button>Upgrade to Pro Plan</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-elegant">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center space-x-2">
              <Code className="h-5 w-5 text-primary" />
              <span>Share & Embed</span>
            </CardTitle>
            <CardDescription>
              Share your form via branded links, WhatsApp, or embed on your website.
            </CardDescription>
          </div>
          <Badge variant="default" className="bg-gradient-accent">Pro</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Branded Links Section */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold flex items-center gap-2">
            <Link className="h-4 w-4 text-primary" />
            Branded Links
          </h4>
          
          <div className="grid gap-3">
            {/* Web Form Link */}
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg border border-border/50">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground mb-1">Web Form Link</p>
                <p className="text-sm font-mono truncate">{formUrl}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => handleCopy(formUrl, 'Web form link')}>
                {copied ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>

            {/* WhatsApp Form Link */}
            <div className="flex items-center gap-3 p-3 bg-emerald-500/5 rounded-lg border border-emerald-500/20">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                  <MessageCircle className="h-3 w-3" />
                  WhatsApp Conversational Link
                </p>
                <p className="text-sm font-mono truncate">{whatsAppLink}</p>
              </div>
              <Button size="sm" variant="outline" className="border-emerald-500/30 hover:bg-emerald-500/10"
                onClick={() => handleCopy(whatsAppLink, 'WhatsApp link')}>
                {copied ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>

            {/* Short Branded Link */}
            <div className="flex items-center gap-3 p-3 bg-primary/5 rounded-lg border border-primary/20">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground mb-1">Short Branded Link</p>
                <p className="text-sm font-mono truncate">{brandedLink}</p>
              </div>
              <Button size="sm" variant="outline" className="border-primary/30 hover:bg-primary/10"
                onClick={() => handleCopy(brandedLink, 'Branded link')}>
                {copied ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Embed Section */}
        <div className="space-y-3 pt-4 border-t border-border">
          <h4 className="text-sm font-semibold flex items-center gap-2">
            <Code className="h-4 w-4 text-primary" />
            Website Embedding
          </h4>

          <div className="bg-muted/50 p-4 rounded-lg space-y-2">
            <div className="flex items-center space-x-2 text-sm">
              <CheckCircle className="h-4 w-4 text-success" />
              <span className="font-medium">Embed anywhere - Website, Landing Pages, Blog Posts</span>
            </div>
            <div className="flex items-center space-x-2 text-sm mt-2">
              <CheckCircle className="h-4 w-4 text-success" />
              <span className="font-medium">Supports file uploads via WhatsApp chat</span>
            </div>
          </div>

          <Tabs defaultValue="iframe" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="iframe">iFrame</TabsTrigger>
              <TabsTrigger value="script">JavaScript</TabsTrigger>
              <TabsTrigger value="html">HTML</TabsTrigger>
            </TabsList>

            <TabsContent value="iframe" className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-2">
                  Simple iframe embed - paste this code anywhere on your website:
                </p>
                <div className="relative bg-muted p-4 rounded-lg">
                  <pre className="text-sm overflow-x-auto">
                    <code>{iframeCode}</code>
                  </pre>
                  <Button size="sm" variant="ghost" className="absolute top-2 right-2"
                    onClick={() => handleCopy(iframeCode, 'iFrame code')}>
                    {copied ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="script" className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-2">
                  JavaScript embed - more control over styling and placement:
                </p>
                <div className="relative bg-muted p-4 rounded-lg">
                  <pre className="text-sm overflow-x-auto">
                    <code>{scriptCode}</code>
                  </pre>
                  <Button size="sm" variant="ghost" className="absolute top-2 right-2"
                    onClick={() => handleCopy(scriptCode, 'JavaScript code')}>
                    {copied ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="html" className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-2">
                  Complete HTML page - use as a standalone contact page:
                </p>
                <div className="relative bg-muted p-4 rounded-lg max-h-96 overflow-y-auto">
                  <pre className="text-sm">
                    <code>{htmlCode}</code>
                  </pre>
                  <Button size="sm" variant="ghost" className="absolute top-2 right-2"
                    onClick={() => handleCopy(htmlCode, 'HTML code')}>
                    {copied ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="text-sm text-muted-foreground">
            Need help? Check our documentation
          </div>
          <Button variant="outline" onClick={() => window.open(formUrl, '_blank')}>
            <ExternalLink className="mr-2 h-4 w-4" />
            Preview Form
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default FormEmbedCode;
